import _ from 'lodash';
import { parseAmount } from '../../../services';
import { Amount } from '../../../api';
import {
    getInstitutionalServiceLineKey,
    getPaymentItemKey,
    getProfessionalServiceLineKey,
} from '../matching-keys';
import { createProfessionalPaymentItemMatcher } from '../professional-payment-matching';
import { getLatestPaymentsByPayerPriority, getPaymentsWithoutReversals } from '../payment-summary';

// Types for the input data
export interface ChartPaymentItem {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes?: string[];
    fromDos?: string;
    paidAmount?: Amount;
    allowedAmount?: Amount;
    adjustments?: Array<{
        group?: string;
        amount?: Amount;
    }>;
}

export interface ChartPayment {
    id?: string;
    status?: string;
    created?: string;
    allowedAmount?: Amount;
    payerAmount?: Amount;
    chargeAmount?: Amount;
    patientAmount?: Amount;
    items?: ChartPaymentItem[];
}

export interface ChartCollectionPayment {
    collectedAmount?: Amount;
}

export interface ChartChargemasterItem {
    id?: string;
    accepted?: Amount;
}

export interface ProfessionalServiceLine {
    x12ControlNumber?: string;
    f24dProcedureCode?: string;
    f24dProcedureModifiers?: string[];
    f24aFromDateOfService?: string;
    f24fChargeAmount?: Amount;
    f24gUnits?: number;
    chargemasterItemId?: string;
}

export interface InstitutionalServiceLine {
    x12ControlNumber?: string;
    f44ProcedureCode?: string;
    f44ModifierCodes?: string[];
    f45FromServiceDate?: string;
    f47TotalChargeAmount?: Amount;
    f46ServiceUnits?: number;
    chargemasterItemId?: string;
}

export interface ChartCalculationResult {
    charge: number;
    contracted: number;
    patientPaid: number;
    patientPending: number;
    payerAllowed: number;
    payerPaid: number;
    payerPending: number;
    totalAdjustments: number;
}

export { getPaymentItemKey, getProfessionalServiceLineKey, getInstitutionalServiceLineKey };

interface ServiceData {
    charge: number;
    accepted: number;
    allowed: number;
    payerPaid: number;
    patientResponsibility: number;
    paymentId?: string;
}

type PaymentItemWithStatus = ChartPaymentItem & {
    paymentId?: string;
    status?: string;
    created?: string;
};

function processPaymentItems(
    payments: ChartPayment[],
    existingControlNumbers: (string | undefined)[]
) {
    const filteredPayments = getPaymentsWithoutReversals(payments);

    // Build payment items with status
    const paymentItems: PaymentItemWithStatus[] = _.flatMap(filteredPayments, p =>
        (p.items || []).map(item => ({
            ...item,
            paymentId: p.id,
            status: p.status,
            created: p.created,
        }))
    );

    // Build map of payment items by key
    const itemsMap = new Map<string, typeof paymentItems>();
    paymentItems.forEach(item => {
        const key = getPaymentItemKey(item, existingControlNumbers) || 'NOT_FOUND';
        const items = itemsMap.get(key) || [];
        items.push(item);
        itemsMap.set(key, items);
    });

    // Get latest payments for patient responsibility calculation
    const latestPayments = getLatestPaymentsByPayerPriority(filteredPayments);

    return { itemsMap, latestPayments, paymentItems };
}

function calculateServiceData(
    foundPaymentItems: PaymentItemWithStatus[],
    latestPayments: ChartPayment[],
    charge: number,
    accepted: number
): ServiceData {
    const paid = foundPaymentItems.reduce((sum, item) =>
        sum + parseAmount(item.paidAmount), 0
    );
    const allowed = Math.max(
        ...foundPaymentItems.map(item => parseAmount(item.allowedAmount)),
        0
    );

    // Calculate patient responsibility from adjustments (only from latest payments)
    const patientResponsibility = foundPaymentItems
        .filter(item => latestPayments.some(p => p.id === item.paymentId))
        .reduce((sum, item) => {
            const patientAdj = (item.adjustments || []).filter(
                adj => adj.group === 'PATIENT_RESPONSIBILITY'
            );
            return sum + patientAdj.reduce((adjSum, adj) =>
                adjSum + parseAmount(adj.amount), 0
            );
        }, 0);

    return {
        charge,
        accepted,
        allowed: isFinite(allowed) ? allowed : 0,
        payerPaid: paid,
        patientResponsibility,
        paymentId: foundPaymentItems[0]?.paymentId,
    };
}

function calculateTotals(
    services: ServiceData[],
    collectionPayments: ChartCollectionPayment[]
): ChartCalculationResult {
    const charge = services.reduce((sum, service) => sum + (service.charge || 0), 0);
    const contracted = services.reduce((sum, service) => sum + (service.accepted || 0), 0);
    const patientPaid = collectionPayments.reduce((sum, cp) =>
        sum + (parseFloat(cp.collectedAmount?.amount || '0') || 0), 0
    );
    const patientPending = services.reduce((sum, service) =>
        sum + (service.patientResponsibility || 0), 0
    ) - patientPaid;
    const payerAllowed = services.reduce((sum, service) => sum + (service.allowed || 0), 0);
    const payerPaid = services.reduce((sum, service) => sum + (service.payerPaid || 0), 0);

    // Check if payer has adjudicated (all services have paymentId)
    const hasUnadjudicatedServices = services.some(service => !service.paymentId || service.paymentId === '');

    let payerPending = 0;
    let totalAdjustments = 0;

    if (hasUnadjudicatedServices) {
        // Payer has not adjudicated yet
        payerPending = charge - payerAllowed;
        totalAdjustments = 0;
    } else {
        // Payer has adjudicated
        payerPending = 0;
        totalAdjustments = charge - payerAllowed;
    }

    return {
        charge,
        contracted,
        patientPaid,
        patientPending: Math.max(0, patientPending),
        payerAllowed,
        payerPaid,
        payerPending,
        totalAdjustments,
    };
}

/**
 * Calculate chart data from professional service lines
 * Uses same matching logic as RecordOfServices
 */
export function calculateChartDataFromProfessionalServices(
    serviceLines: ProfessionalServiceLine[],
    payments: ChartPayment[],
    collectionPayments: ChartCollectionPayment[],
    chargemasterItems: ChartChargemasterItem[]
): ChartCalculationResult {
    const existingControlNumbers = serviceLines.map(line => line.x12ControlNumber);
    const { latestPayments, paymentItems } = processPaymentItems(payments, existingControlNumbers);
    const professionalMatcher = createProfessionalPaymentItemMatcher(paymentItems, existingControlNumbers);

    const services = serviceLines.map(line => {
        const foundPaymentItems = professionalMatcher.matchLine(line);

        const charge = parseAmount(line.f24fChargeAmount);
        const chargemasterItem = _.find(chargemasterItems, { id: line.chargemasterItemId });
        const accepted = parseAmount(chargemasterItem?.accepted) * (line.f24gUnits || 1);

        return calculateServiceData(foundPaymentItems, latestPayments, charge, accepted);
    });

    return calculateTotals(services, collectionPayments);
}

/**
 * Calculate chart data from institutional service lines
 * Uses same matching logic as RecordOfServices
 */
export function calculateChartDataFromInstitutionalServices(
    serviceLines: InstitutionalServiceLine[],
    payments: ChartPayment[],
    collectionPayments: ChartCollectionPayment[],
    chargemasterItems: ChartChargemasterItem[]
): ChartCalculationResult {
    const existingControlNumbers = serviceLines.map(line => line.x12ControlNumber);
    const { itemsMap, latestPayments } = processPaymentItems(payments, existingControlNumbers);

    const services = serviceLines.map(line => {
        // Try to find matching payment items with fallback
        let lineKey = getInstitutionalServiceLineKey(line);
        let matchingItems = lineKey ? itemsMap.get(lineKey) : undefined;
        if (!matchingItems) {
            // Fallback: try without control number
            lineKey = getInstitutionalServiceLineKey({ ...line, x12ControlNumber: undefined });
            matchingItems = lineKey ? itemsMap.get(lineKey) : undefined;
        }
        const foundPaymentItems = matchingItems || [];

        const charge = parseAmount(line.f47TotalChargeAmount);
        const chargemasterItem = _.find(chargemasterItems, { id: line.chargemasterItemId });
        const accepted = parseAmount(chargemasterItem?.accepted) * (line.f46ServiceUnits || 1);

        return calculateServiceData(foundPaymentItems, latestPayments, charge, accepted);
    });

    return calculateTotals(services, collectionPayments);
}

/**
 * Returns empty/zero chart data for cases where no template is available
 */
export function getEmptyChartData(): ChartCalculationResult {
    return {
        charge: 0,
        contracted: 0,
        patientPaid: 0,
        patientPending: 0,
        payerAllowed: 0,
        payerPaid: 0,
        payerPending: 0,
        totalAdjustments: 0,
    };
}
